import type { MouseEvent } from "react";
import type { GlobeCluster } from "./globeTypes";
import { ResidentActor } from "../actors/ResidentActor";
import { MapPortrait } from "./MapResidentCard";
import { citiesOf, isLastKnown, placeText } from "./publicMapModel";
import { nowLine, presenceGroup } from "../residentView";

/**
 * 上次的位置（last_known_area）的小角色：位置是上次的，不演「此刻」，也不冒 z 压住「上次在这」的牌子（UI2-CITY-ACTORS-117，设计评审中 9）。
 * 只画静止的 TA（在家、说不清睡没睡那一幕：不演、不挂小标），不用说不清在哪那层暗化——「上次在这」自己已经把头像淡到 0.72，
 * 再叠一层 0.55 就认不出是谁了（设计评审第二轮中 2）。
 */
const LAST_KNOWN_ACT = { presence: "at_home", asleep: null } as const;

/** 一处有几位时读屏怎么说：几位的名字。 */
export function clusterNames(cluster: GlobeCluster): string {
  return cluster.residents.map((resident) => resident.name).join("、");
}

/**
 * 地区层的标记（真实身份头像）：小尾巴的尖落在公开坐标上（外层按坐标定位，这里往上让出自己的高度）。
 * - 一位：头像；圈和小尾巴按此刻在哪上色（data-zone，和短卡同一套）；在外、路上不只靠颜色：小角色到访挂指南针、在路上走着
 *   （UI2-CITY-ACTORS-117 起；原来另挂的旅行小标 UI-REVIEW110-04 去掉了，两样标记挤在一颗 38px 的头像上，6c2b 10-16 同意）；
 *   上次的位置（last_known_area）描虚线、写“上次在这”，读屏也这么说；
 * - 地区层里同一格的几位已错开成几处（spreadClusters），一处只有一位；挨得近的由 MergedPin 合成。
 * - 一位的这一处是个会动的小角色（UI2-CITY-ACTORS-117，接 6796 的 ResidentActor）：头像圈原样交进去当 portrait（圈照旧裁圆），
 *   在家不挂小标（城市层只给例外挂，I 10-16），睡着冒 zzz、脸暗一点；到访挂实底的指南针；在路上走着；说不清暗着；
 *   按钮、读屏名、data-cluster 都不变（页面按它找这一处）。
 *   上次的位置（last_known_area）不演此刻，只画静止的 TA。页面有短卡、名单、就近面板或抽屉盖着地图时 paused，小角色停在当下。
 * data-map-interactive：按在标记上不拖动底图（shared/map 与 Map2D 都认这个属性）。
 */
export function SpotPin({ cluster, expanded, onActivate, paused = false }: { cluster: GlobeCluster; expanded: boolean; onActivate: (cluster: GlobeCluster, from: HTMLElement) => void; paused?: boolean }) {
  const onClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.currentTarget.focus({ preventScroll: true });
    onActivate(cluster, event.currentTarget);
  };
  const resident = cluster.residents[0];
  const last = isLastKnown(resident);
  const zone = presenceGroup(resident.presence);
  return (
    <button
      type="button"
      className={`ps-pmap-marker ps-pmap-pin${last ? " is-last-known" : ""}`}
      data-map-interactive
      data-pet-id={resident.pet_id}
      data-cluster={cluster.key}
      data-zone={zone}
      aria-label={`${resident.name}：${nowLine(resident)}；${placeText(resident)}`}
      aria-haspopup="dialog"
      aria-expanded={expanded}
      onClick={onClick}
    >
      <ResidentActor
        resident={last ? LAST_KNOWN_ACT : resident}
        size={38}
        paused={paused}
        portrait={
          <span className="ps-pmap-pin__face" aria-hidden="true">
            <MapPortrait resident={resident} size={38} />
          </span>
        }
      />
      {last ? (
        <span className="ps-pmap-pin__note" aria-hidden="true">
          上次在这
        </span>
      ) : null}
    </button>
  );
}

/** 示意图上挨得太近、合成一个的标记（几处不同坐标）：第一位的头像 + 一共几位；点开由页面列名单，放大地图就分开。 */
export function MergedPin({ cluster, expanded, onActivate }: { cluster: GlobeCluster; expanded: boolean; onActivate: (cluster: GlobeCluster, from: HTMLElement) => void }) {
  // 4b（I 10-14）：跨了几座城的，点开先按城选
  const cities = citiesOf(cluster.residents).cities.map((c) => c.city);
  return (
    <button
      type="button"
      className="ps-pmap-marker ps-pmap-pin is-stack is-merged"
      data-map-interactive
      data-cluster={cluster.key}
      aria-label={cities.length >= 2 ? `这一带挨得很近的 ${cluster.residents.length} 位，在${cities.join("、")}：${clusterNames(cluster)}。点开先选城，放大地图能分开看` : `这一带挨得很近的 ${cluster.residents.length} 位：${clusterNames(cluster)}。点开选一位，放大地图能分开看`}
      aria-haspopup="dialog"
      aria-expanded={expanded}
      onClick={(event) => {
        event.currentTarget.focus({ preventScroll: true });
        onActivate(cluster, event.currentTarget);
      }}
    >
      <span className="ps-pmap-pin__face" aria-hidden="true">
        <MapPortrait resident={cluster.residents[0]} size={38} />
      </span>
      <span className="ps-pmap-pin__count" aria-hidden="true">
        {cluster.residents.length}
      </span>
    </button>
  );
}

/** 总览（2D 占位）的聚合点：只写人数；点了交给页面（一位出短卡，几位出名单）。 */
export function CountDot({ cluster, label, selected, onSelect }: { cluster: GlobeCluster; label: string; selected: boolean; onSelect: (cluster: GlobeCluster) => void }) {
  return (
    <button
      type="button"
      className={`ps-pmap-marker ps-pmap-dot${selected ? " is-selected" : ""}`}
      data-map-interactive
      data-cluster={cluster.key}
      data-count={cluster.residents.length}
      aria-label={label}
      aria-pressed={selected}
      onClick={(event) => {
        event.currentTarget.focus({ preventScroll: true });
        onSelect(cluster);
      }}
    >
      {cluster.residents.length}
    </button>
  );
}
